import * as Sentry from '@sentry/react';
import axios from '../../../api-client.ts';
import clsx from 'clsx';
import { useFormik } from 'formik';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import * as Yup from 'yup';
import i18n from '../../../../_mesomb/i18n';
import ButtonIndicator from '../../../../_mesomb/partials/components/ButtonIndicator.tsx';
import { AuthenticationError } from '../../../../_mesomb/utils/execptions.ts';
import { AuthError, AuthProps } from '../../../../types';
import LoginOptions from './LoginOptions.tsx';

const loginSchema = Yup.object().shape({
  login: Yup.string()
    .email(i18n.t('Wrong email format'))
    .max(255, i18n.t('Minimum_characters', { min: 255 }))
    .required(i18n.t('Field_is_required')),
  password: Yup.string()
    .max(200, i18n.t('Maximum_characters', { max: 200 }))
    .required(i18n.t('Field_is_required')),
});
type FormLoginValues = Yup.InferType<typeof loginSchema>;

function Login({ onLogin, configs }: AuthProps) {
  const { t } = useTranslation();
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  const formik = useFormik<FormLoginValues>({
    initialValues: {
      login: params?.get('email') ?? '',
      password: '',
    },
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting, setErrors }) => {
      setSubmitting(true);
      setStatus(undefined);
      try {
        await onLogin('login', {
          email: values.login,
          password: values.password,
        });
        setSubmitting(false);
      } catch (error) {
        setSubmitting(false);
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
          const errors = (error.response.data.errors as AuthError[]).reduce<Record<string, string>>(
            (acc, item) => ({
              ...acc,
              [item.param ?? item.code]: item.message,
            }),
            {}
          );
          setErrors(errors);
          if (errors.invalid_login) {
            setStatus(errors.invalid_login);
          }
        } else if (error instanceof AuthenticationError) {
          if (error.message && error.message.length > 0) {
            setStatus(error.message);
          }
          setErrors(error.errors);
        } else if (error instanceof Error) {
          if (error.message) {
            setStatus(error.message);
          }
          Sentry.captureException(error);
        }
      }
    },
  });

  return (
    <>
      <form
        className='form w-100'
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_login_signin_form'
      >
        {/* begin::Heading */}
        <div className='text-center mb-11'>
          <h1 className='text-gray-900 fw-bolder mb-3'>{t('Sign In')}</h1>
          <div className='text-gray-500 fw-semibold fs-6'>
            {t('Welcome to MeSomb')}
          </div>
        </div>
        {/* begin::Heading */}

        {/* begin::Login options */}
        <LoginOptions configs={configs} onLogin={onLogin} />
        {/* end::Login options */}

        {/* begin::Separator */}
        <div className='separator separator-content my-14'>
          <span className='w-200px text-gray-500 fw-semibold fs-7'>
            {t('Or with password')}
          </span>
        </div>
        {/* end::Separator */}

        {formik.status ? (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        ) : null}

        {/* begin::Form group */}
        <div className='fv-row mb-8'>
          <label className='form-label fs-6 fw-bolder text-gray-900'>
            {t('Email')}
          </label>
          <input
            placeholder={t('Email address')}
            {...formik.getFieldProps('login')}
            className={clsx(
              'form-control bg-transparent',
              { 'is-invalid': formik.touched.login && formik.errors.login },
              {
                'is-valid': formik.touched.login && !formik.errors.login,
              }
            )}
            type='email'
            name='login'
            data-cy={'login-email'}
            // autoComplete='off'
          />
          {formik.touched.login && formik.errors.login && (
            <div className='fv-plugins-message-container form-text text-danger'>
              <span role='alert'>{formik.errors.login}</span>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='fv-row mb-3'>
          <label className='form-label fw-bolder text-gray-900 fs-6 mb-0'>
            {t('Password')}
          </label>
          <input
            type='password'
            autoComplete='off'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': formik.touched.password && formik.errors.password,
              },
              {
                'is-valid': formik.touched.password && !formik.errors.password,
              }
            )}
            placeholder={t('Enter your password')}
            data-cy={'login-password'}
          />
          {formik.touched.password && formik.errors.password && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.password}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Wrapper */}
        <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
          <div />

          {/* begin::Link */}
          <Link to='/auth/forgot-password' className='link-primary'>
            {t('Forgot Password ?')}
          </Link>
          {/* end::Link */}
        </div>
        {/* end::Wrapper */}

        {/* begin::Action */}
        <div className='d-grid mb-10'>
          <ButtonIndicator
            label={t('Sign In')}
            type={'submit'}
            loading={formik.isSubmitting}
            disabled={formik.isSubmitting || !formik.isValid}
          />
        </div>
        {/* end::Action */}

        <div className='text-gray-500 text-center fw-semibold fs-6'>
          {t('Not a Member yet?')}{' '}
          <Link to={`/auth/signup${params.has('email') ? `?email=${params.get('email')}` : ''}`} className='link-primary'>
            {t('Sign up')}
          </Link>
        </div>
      </form>
      <Helmet>
        <title>{t('Authentication')}</title>
      </Helmet>
    </>
  );
}

export default Login;
